import axios from 'axios';

function safeToJson(error) {
  if (!error || typeof error.toJSON !== 'function') return {};
  try {
    return error.toJSON() || {};
  } catch {
    return {};
  }
}

function normalizeHttpStatus(value) {
  const status = Number(value);
  return Number.isInteger(status) && status >= 100 && status <= 599 ? status : 0;
}

function parseHttpStatusFromText(text) {
  if (!text) return 0;
  const source = String(text);
  const matched =
    source.match(/status code:?\s+(\d{3})/i) ||
    source.match(/\b(http\s*)?status:?\s*(\d{3})\b/i) ||
    source.match(/\b(\d{3})\s+[a-z][a-z\s-]*/i);
  return normalizeHttpStatus(matched?.[2] || matched?.[1]);
}

function findNestedHttpStatus(source, seen = new Set()) {
  if (!source || typeof source !== 'object' || seen.has(source)) return 0;
  seen.add(source);

  const directStatus =
    normalizeHttpStatus(source.status) ||
    normalizeHttpStatus(source.statusCode) ||
    normalizeHttpStatus(source.httpStatus) ||
    normalizeHttpStatus(source.httpStatusCode) ||
    normalizeHttpStatus(source.code) ||
    normalizeHttpStatus(source.httpCode);

  if (directStatus) return directStatus;

  const messageStatus =
    parseHttpStatusFromText(source.message) ||
    parseHttpStatusFromText(source.statusText) ||
    parseHttpStatusFromText(source.responseText);

  if (messageStatus) return messageStatus;

  const nestedKeys = ['response', 'request', 'data', 'error', 'cause', 'originalError'];
  for (const key of nestedKeys) {
    const nestedStatus = findNestedHttpStatus(source[key], seen);
    if (nestedStatus) return nestedStatus;
  }

  return 0;
}

export function extractApiStatusCode(error) {
  const sourceError = error?.value || error;
  const serialized = safeToJson(sourceError);
  const data = sourceError?.response?.data;
  const axiosStatus = axios.isAxiosError(sourceError)
    ? normalizeHttpStatus(sourceError.response?.status)
    : 0;
  const statusSource = [
    typeof sourceError === 'string' ? sourceError : '',
    sourceError?.message,
    serialized?.message,
    sourceError?.response?.statusText,
    serialized?.statusText,
    typeof data === 'string' ? data : '',
    data?.message
  ]
    .filter(Boolean)
    .join(' ');

  return (
    axiosStatus ||
    findNestedHttpStatus(sourceError) ||
    findNestedHttpStatus(serialized) ||
    parseHttpStatusFromText(statusSource)
  );
}

export function extractApiEndpoint(error) {
  if (!error || typeof error === 'string') return '';

  const serialized = safeToJson(error);
  const config = error?.config || error?.response?.config || serialized?.config || {};
  const method = String(config?.method || error?.method || '').trim().toUpperCase();
  const rawUrl = String(
    config?.url ||
      error?.url ||
      error?.request?.responseURL ||
      error?.response?.request?.responseURL ||
      serialized?.url ||
      ''
  ).trim();

  if (!rawUrl) return '';

  const baseURL = String(config?.baseURL || '').trim();
  const source = rawUrl.startsWith('http') ? rawUrl : `${baseURL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
  let endpoint = rawUrl;

  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://localhost';
    const parsed = new URL(source, origin);
    endpoint = parsed.pathname || rawUrl;
  } catch {
    endpoint = rawUrl.split('?')[0] || rawUrl;
  }

  return [method, endpoint].filter(Boolean).join(' ');
}
